import React from 'react';
import { imageUrl } from '../../utils';

interface Requirement {
  icon: React.ReactNode;
  text: string;
}

interface ApproachSection {
  title: string;
  description: string;
  icon?: string;
  alt?: string;
}

interface ChallengeApproachProps {
  challengeTitle: string;
  challengeDescription: string;
  requirementsTitle: string;
  requirements: Requirement[];
  approachTitle: string;
  approachSections: ApproachSection[];
  testimonial?: {
    initials?: string;
    name: string;
    role: string;
    quote: string;
    icon?: string;
  };
}

const ChallengeApproach = ({
  challengeTitle,
  challengeDescription,
  requirementsTitle,
  requirements,
  approachTitle,
  approachSections,
  testimonial,
}: ChallengeApproachProps) => {
  return (
    <section className="container mx-auto px-4 py-20">
      <div className="grid md:grid-cols-2 gap-16 items-start">
        {/* Left side - Sticky */}
        <div className="md:sticky md:top-36 space-y-8">
          <div className="group">
            <div className="flex items-baseline gap-3 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-8 h-8 text-[#FAC72E] flex-shrink-0 mt-1"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="6"></circle>
                <circle cx="12" cy="12" r="2"></circle>
              </svg>
              <h2 className="text-4xl font-bold text-[#263238]" style={{ fontFamily: 'var(--fontBase)' }}>
                {challengeTitle}
              </h2>
            </div>
            <p className="text-lg text-[#728F8B] leading-relaxed" style={{ fontFamily: 'var(--fontPrimary)' }}>
              {challengeDescription}
            </p>
          </div>

          {/* Requirements Card */}
          <div className="relative group/card p-8 rounded-3xl bg-[#FAC72E] overflow-hidden shadow-xl">
            <div className="relative">
              <div className="flex items-baseline gap-2 mb-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5 text-[#000000] flex-shrink-0"
                  style={{ marginTop: '2px' }}
                >
                  <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
                </svg>
                <h3 className="text-xl font-bold text-[#000000]" style={{ fontFamily: 'var(--fontBase)' }}>
                  {requirementsTitle}
                </h3>
              </div>
              <ul className="space-y-4">
                {requirements.map((req, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#ECF0FF] flex items-center justify-center text-[#000000] text-sm font-bold flex-shrink-0">
                      {req.icon}
                    </div>
                    <span className="text-[#000000] pt-1.5" style={{ fontFamily: 'var(--fontPrimary)' }}>
                      {req.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Right side - Scrollable */}
        <div className="space-y-16">
          <div>
            <h2 className="text-4xl font-bold text-[#263238] mb-10" style={{ fontFamily: 'var(--fontBase)' }}>
              {approachTitle}
            </h2>
            <div className="space-y-8">
              {approachSections.map((section, index) => (
                <div key={index} className="space-y-4">
                  <div className="flex items-center gap-4">
                    {/* Icon on the left */}
                    {section.icon && (
                      <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 52, height: 52 }}>
                        <img 
                          loading="lazy" 
                          src={imageUrl(section.icon)} 
                          alt={section.alt || section.title}
                          className="w-full h-full object-contain"
                          style={{ filter: 'brightness(0) saturate(100%) invert(68%) sepia(1%) saturate(0%) hue-rotate(314deg) brightness(96%) contrast(89%)' }}
                        />
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-[#263238] flex-1 mb-0" style={{ fontFamily: 'var(--fontBase)' }}>
                      {section.title}
                    </h3>
                  </div>
                  <p className="text-[#728F8B] leading-relaxed" style={{ fontFamily: 'var(--fontPrimary)' }}>
                    {section.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial Card */}
          {testimonial && (
            <div className="p-8 rounded-3xl bg-[#F8F8F8] border-2 border-gray-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {testimonial.icon ? (
                    <img src={imageUrl(testimonial.icon)} alt="" className="w-full h-full object-cover rounded-full" />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-[#FA832E] to-[#F5CA09] flex items-center justify-center text-2xl font-bold text-white">
                      {testimonial.initials}
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-bold text-[#263238] text-lg" style={{ fontFamily: 'var(--fontBase)' }}>
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-[#728F8B]" style={{ fontFamily: 'var(--fontPrimary)' }}>
                    {testimonial.role}
                  </div>
                </div>
              </div>
              <p className="text-lg text-[#263238] leading-relaxed" style={{ fontFamily: 'var(--fontPrimary)' }}>
                "{testimonial.quote}"
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ChallengeApproach;

